import { useCallback, useEffect, useMemo, useState } from 'react';

/**
 * @deprecated
 */
export const useSelected = <T extends Record<K, unknown>, K extends keyof T>(
  rows: T[],
  identifyKey: K,
): [
  readonly T[K][],
  (key: T[K]) => void,
  (keys: T[K][]) => void,
  (checked: boolean) => void,
] => {
  const [selected, setSelected] = useState<readonly T[K][]>([]);

  useEffect(() => {
    setSelected(previousSelected =>
      previousSelected.filter(skey =>
        rows.find(row => row[identifyKey] === skey),
      ),
    );
  }, [rows, identifyKey]);

  const selectAll = useCallback(
    (checked: boolean) => {
      if (checked) {
        const newSelected = rows.map(n => n[identifyKey]);
        setSelected(newSelected);
        return;
      }
      setSelected([]);
    },
    [identifyKey, rows],
  );

  const selectOne = useCallback(
    (key: T[K]) => {
      const selectedIndex = selected.indexOf(key);
      let newSelected: readonly T[K][] = [];

      if (selectedIndex === -1) {
        newSelected = newSelected.concat(selected, key);
      } else if (selectedIndex === 0) {
        newSelected = newSelected.concat(selected.slice(1));
      } else if (selectedIndex === selected.length - 1) {
        newSelected = newSelected.concat(selected.slice(0, -1));
      } else if (selectedIndex > 0) {
        newSelected = newSelected.concat(
          selected.slice(0, selectedIndex),
          selected.slice(selectedIndex + 1),
        );
      }

      setSelected(newSelected);
    },
    [selected],
  );

  const selectMany = useCallback((keys: T[K][]) => {
    setSelected(prev => {
      const notIncludedIds = prev.filter(key => !keys.includes(key));
      return [...notIncludedIds, ...keys];
    });
  }, []);

  return [selected, selectOne, selectMany, selectAll];
};

/**
 * server side selected
 * @param dataToSelect 전체선택 때 선택할 대상
 * @param identifyKey id key
 * @returns [모든 페이지의 선택된 어레이, 하나 선택 (토글), 화면에 보이는 것 전체 선택, 전체 선택 취소]
 */
export const useServerSideSelected = <
  T extends Record<K, unknown>,
  K extends keyof T,
>(
  dataToSelect: T[],
  identifyKey: K,
): {
  selected: readonly T[K][];
  selectedData: T[];
  selectOne: (key: T[K]) => void;
  selectMany: (keys: T[K][]) => void;
  selectAll: (checked: boolean, selectables?: T[]) => void;
  unselectAll: () => void;
} => {
  const [selected, setSelected] = useState<Set<T[K]>>(new Set<T[K]>());
  const [selectedData, setSelectedData] = useState<Map<T[K], T>>(
    new Map<T[K], T>(),
  );

  useEffect(() => {
    setSelected(new Set());
    setSelectedData(new Map());
  }, [identifyKey]);

  const selectedAsArray = useMemo(() => {
    return Array.from(selected);
  }, [selected]);
  const selectedDataAsArray = useMemo(() => {
    return Array.from(selectedData.values());
  }, [selectedData]);

  const selectAll = useCallback(
    (checked: boolean, selectables = dataToSelect) => {
      const newSet = new Set(selected);
      const newDataMap = new Map<T[K], T>(selectedData);

      if (checked) {
        if (selectables.every(data => newSet.has(data[identifyKey]))) {
          selectables.forEach(data => {
            newSet.delete(data[identifyKey]);
            newDataMap.delete(data[identifyKey]);
          });
        } else {
          selectables.forEach(data => {
            newSet.add(data[identifyKey]);
            newDataMap.set(data[identifyKey], data);
          });
        }
      } else {
        selectables.forEach(data => {
          newSet.delete(data[identifyKey]);
          newDataMap.delete(data[identifyKey]);
        });
      }
      setSelected(newSet);
      setSelectedData(newDataMap);
    },
    [dataToSelect, selected, selectedData, identifyKey],
  );

  const selectOne = useCallback(
    (key: T[K]) => {
      const newSet = new Set(selected);
      const newDataSet = new Map<T[K], T>(selectedData);
      const isSelected = newSet.has(key);
      const targetData = dataToSelect.find(d => d[identifyKey] === key) as T;
      if (isSelected) {
        newSet.delete(key);
        newDataSet.delete(key);
      } else {
        newSet.add(key);
        newDataSet.set(key, targetData);
      }
      setSelected(newSet);
      setSelectedData(newDataSet);
    },
    [dataToSelect, identifyKey, selected, selectedData],
  );

  /**
   * 병원 검색 시, 발급 신청 가능한 study를 자동으로 선택해 줄 때 필요
   * 25.04.02 study 자동 선택 기능이 삭제됨
   * @deprecated
   */
  const selectMany = useCallback((keys: T[K][]) => {
    setSelected(prev => {
      const newSet = new Set(prev);
      keys.map(key => newSet.add(key));
      return newSet;
    });
  }, []);

  const unselectAll = useCallback(() => {
    setSelected(prev => (prev.size === 0 ? prev : new Set()));
    setSelectedData(prev => (prev.size === 0 ? prev : new Map()));
  }, []);

  return {
    selected: selectedAsArray,
    selectedData: selectedDataAsArray,
    selectOne,
    selectMany,
    selectAll,
    unselectAll,
  };
};

export const useSelectedIndices = (): {
  selectIndex: (index: number, checked: boolean) => void;
  selectAllIndicesInCurrentPage: (indices: number[], checked: boolean) => void;
  unselect: () => void;
  handlePaginationAfterDelete: (
    currentPage: number,
    sizeOfPage: number,
    currentPageItemCount: number,
    setPage: (page: number) => void,
  ) => void;
} => {
  const [selectedIndices, setSelectedIndices] = useState<number[]>([]);

  const unselect = useCallback(() => {
    setSelectedIndices([]);
  }, []);

  const handlePaginationAfterDelete = useCallback(
    (
      currentPage: number,
      sizeOfPage: number,
      currentPageItemCount: number,
      setPage: (page: number) => void,
    ) => {
      if (!selectedIndices.length) {
        return;
      }

      const currentPageStartIndex = currentPage * sizeOfPage;
      const currentPageEndIndex = (currentPage + 1) * sizeOfPage;

      const countsOfPrevPageIndices = selectedIndices.filter(
        index => currentPageStartIndex > index,
      ).length;
      const countOfCurrentPageIndices = selectedIndices.filter(
        index => index >= currentPageStartIndex && index < currentPageEndIndex,
      ).length;

      const isCurrnetPageEmtpyAfterDelete =
        countOfCurrentPageIndices === currentPageItemCount;
      const pageOffset =
        Math.ceil(countsOfPrevPageIndices / sizeOfPage) +
        (isCurrnetPageEmtpyAfterDelete ? 1 : 0);

      setPage(Math.max(currentPage - pageOffset, 0));
      unselect();
    },
    [selectedIndices, unselect],
  );

  const selectIndex = useCallback(
    (index: number, checked: boolean) =>
      setSelectedIndices(prev => {
        const newIndices = [...prev];
        if (checked) {
          newIndices.push(index);
        } else {
          newIndices.splice(newIndices.indexOf(index), 1);
        }
        return newIndices;
      }),
    [],
  );

  const selectAllIndicesInCurrentPage = useCallback(
    (indices: number[], checked: boolean) => {
      setSelectedIndices(prev => {
        const newIndices = [...prev];
        if (checked) {
          return [
            ...newIndices.filter(index => !indices.includes(index)),
            ...indices,
          ];
        } else {
          newIndices.splice(newIndices.indexOf(indices[0]), indices.length);
          return newIndices;
        }
      });
    },
    [],
  );

  return {
    selectIndex,
    selectAllIndicesInCurrentPage,
    unselect,
    handlePaginationAfterDelete,
  };
};
